import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

export interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default';
  loading?: boolean;
}

/**
 * Reusable KPI Card component for displaying key performance indicators
 *
 * @param title - The title/label of the KPI
 * @param value - The main value to display
 * @param subtitle - Optional subtitle text below the value
 * @param icon - Optional icon to display in the top right
 * @param trend - Optional trend indicator ('up', 'down', 'stable')
 * @param trendValue - Optional text to display next to trend indicator
 * @param color - Optional color theme for the card
 * @param loading - Whether to show loading skeleton
 */
const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  color = 'default',
  loading = false,
}) => {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon color="success" fontSize="small" />;
      case 'down':
        return <TrendingDownIcon color="error" fontSize="small" />;
      case 'stable':
        return <TrendingFlatIcon color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'success.main';
      case 'down':
        return 'error.main';
      case 'stable':
        return 'warning.main';
      default:
        return 'text.secondary';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="80%" height={40} sx={{ mt: 1 }} />
          {subtitle && <Skeleton variant="text" width="40%" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        borderLeft: color !== 'default' ? 4 : 0,
        borderColor: color !== 'default' ? `${color}.main` : 'transparent',
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          {icon && (
            <Box sx={{ color: color !== 'default' ? `${color}.main` : 'text.secondary' }}>
              {icon}
            </Box>
          )}
        </Box>

        <Typography
          variant="h5"
          component="div"
          fontWeight="bold"
          sx={{ mt: 1, mb: subtitle || trend ? 1 : 0 }}
        >
          {value}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}

        {trend && (
          <Box display="flex" alignItems="center" mt={1}>
            {getTrendIcon()}
            {trendValue && (
              <Typography
                variant="body2"
                color={getTrendColor()}
                sx={{ ml: 0.5 }}
              >
                {trendValue}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;
